// src/CursorTrail.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import './CursorTrail.css';

let particleIdCounter = 0;

const TrailParticle = ({ id, initialX, initialY, onComplete }) => {
  const [style, setStyle] = useState({
    left: `${initialX}px`,
    top: `${initialY}px`,
    backgroundColor: Math.random() < 0.7 ? 'rgba(247, 146, 30, 0.7)' : 'rgba(100, 150, 255, 0.7)', // Orange or blue
    opacity: 1,
    transform: 'scale(1) translate(-50%, -50%)', // Start at full size
  });

  useEffect(() => {
    const fadeOutTimer = setTimeout(() => {
      setStyle(prev => ({
        ...prev,
        opacity: 0,
        transform: `scale(0.3) translate(-50%, -50%) translateX(${Math.random() * 20 - 10}px) translateY(${Math.random() * 20 - 10}px)`, // Shrink and scatter slightly
      }));
    }, 50); // Start fading quickly

    const removeTimer = setTimeout(() => {
      onComplete(id);
    }, 600); // 0.05s + 0.5s transition + buffer

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(removeTimer);
    };
  }, [id, onComplete]);

  return <div className="trail-particle" style={style}></div>;
};


const CursorTrail = () => {
  const [particles, setParticles] = useState([]);
  const lastPosition = useRef({ x: 0, y: 0 });
  const trailTimeoutRef = useRef(null);
  const isThrottled = useRef(false); // For throttling particle creation

  const addParticle = useCallback((x, y) => {
    const newId = particleIdCounter++;
    setParticles(prev => [...prev, { id: newId, x, y }]);
    // Limit number of particles to prevent performance issues
    if (particles.length > 30) { // Max 30 particles
        setParticles(prev => prev.slice(1));
    }
  }, [particles.length]); // Add particles.length to dependencies

  const removeParticle = useCallback((idToRemove) => {
    setParticles(prev => prev.filter(p => p.id !== idToRemove));
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isThrottled.current) return; // If throttled, do nothing

      isThrottled.current = true; // Set throttle flag
      setTimeout(() => { isThrottled.current = false; }, 30); // Throttle for 30ms

      const distance = Math.hypot(e.clientX - lastPosition.current.x, e.clientY - lastPosition.current.y);
      if (distance > 5) { // Only add particle if mouse moved enough
        addParticle(e.clientX, e.clientY);
        lastPosition.current = { x: e.clientX, y: e.clientY };
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (trailTimeoutRef.current) clearTimeout(trailTimeoutRef.current);
    };
  }, [addParticle]);

  return (
    <>
      {particles.map(p => (
        <TrailParticle
          key={p.id}
          id={p.id}
          initialX={p.x}
          initialY={p.y}
          onComplete={removeParticle}
        />
      ))}
    </>
  );
};

export default CursorTrail;